import type {
  CustoEscopoPadrao,
  EscopoPadraoLinha,
  PeriodicidadeEscopoPadrao,
} from "@/modules/financeiro-configuracoes";
import { ESCOPOS_PADRAO_SEM_VALOR } from "./escopos-padrao.constants";
import type { CelulasEscopoPadrao } from "./types-escopos-padrao";

const HORAS_FORMATTER = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });

const VALOR_FORMATTER = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ALTERACAO_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const texto = (valor: string | null | undefined) =>
  valor && valor.trim() ? valor : ESCOPOS_PADRAO_SEM_VALOR;

function formatarComMoeda(valor: number, moeda: string): string {
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: moeda })
      .format(valor)
      .replace(/\u00a0/g, " ");
  } catch {
    return `${moeda} ${VALOR_FORMATTER.format(valor)}`;
  }
}

export function formatarCusto(valor: number | null, moeda?: string): string {
  if (valor === null) return ESCOPOS_PADRAO_SEM_VALOR;
  return moeda ? formatarComMoeda(valor, moeda) : VALOR_FORMATTER.format(valor);
}

export const formatarHoras = (horas: number | null): string => {
  if (horas === null) return ESCOPOS_PADRAO_SEM_VALOR;
  if (horas >= 1000 && horas % 1000 === 0) return `${HORAS_FORMATTER.format(horas / 1000)}k h`;
  return `${HORAS_FORMATTER.format(horas)} h`;
};

function formatarAlteracao({ em, por }: EscopoPadraoLinha["ultimaAlteracao"]): string {
  const data = new Date(em);
  const quando = Number.isNaN(data.getTime())
    ? em
    : ALTERACAO_FORMATTER.format(data).replace(",", "");
  return `${quando} · ${por}`;
}

export function montarCelulas(item: EscopoPadraoLinha): CelulasEscopoPadrao {
  const custo = (periodicidade: PeriodicidadeEscopoPadrao): CustoEscopoPadrao | undefined =>
    item.custos.find((c) => c.periodicidade === periodicidade);
  const servicos = (c?: CustoEscopoPadrao) =>
    formatarCusto(c?.valorServicos ?? null, c?.moedaServicos?.codigo);
  const materiais = (c?: CustoEscopoPadrao) =>
    formatarCusto(c?.valorMateriais ?? null, c?.moedaMateriais?.codigo);
  const intermediaria = custo("INTERMEDIARIA");
  const overhaul = custo("OVERHAUL");
  const unica = custo("UNICA");

  return {
    tipoObra: `${item.tipoObra.codigo} · ${item.tipoObra.nome}`,
    grupo: texto(item.grupo?.nome),
    subgrupo: texto(item.subgrupo?.nome),
    embarcacao: texto(item.embarcacao?.nome),
    escopo: texto(item.escopo),
    equipamento: texto(item.equipamento?.tag),
    situacao: item.situacao === "ATIVO" ? "Ativo" : "Inativo",
    fabricanteModelo: texto(
      [item.equipamento?.fabricante, item.equipamento?.modelo].filter(Boolean).join(" "),
    ),
    intermediariaHoras: formatarHoras(intermediaria?.horas ?? null),
    intermediariaServicos: servicos(intermediaria),
    intermediariaMateriais: materiais(intermediaria),
    overhaulHoras: formatarHoras(overhaul?.horas ?? null),
    overhaulServicos: servicos(overhaul),
    overhaulMateriais: materiais(overhaul),
    servicos: servicos(unica),
    materiais: materiais(unica),
    ultimaAlteracao: formatarAlteracao(item.ultimaAlteracao),
  };
}
